'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

const categories = [
{ id: 'all', label: 'All Picks' },
{ id: 'wellness', label: 'Wellness' },
{ id: 'beauty', label: 'Beauty' },
{ id: 'personal-care', label: 'Personal Care' }];


const mariasFavorites = [
{
  id: 'mf-001',
  name: 'Lumière Radiance Serum',
  category: 'beauty',
  price: 89,
  memberPrice: 72,
  pv: 45,
  badge: 'Maria\'s #1 Pick',
  image: "https://images.unsplash.com/photo-1632127649343-1f27f424bf0b",
  alt: 'Elegant glass serum bottle with gold dropper on white marble surface',
  mariaNote: '"This serum transformed my skin within 3 weeks. Non-negotiable in my morning routine."',
  inStock: true,
  featured: true
},
{
  id: 'mf-002',
  name: 'VitaCore Daily Essentials',
  category: 'wellness',
  price: 65,
  memberPrice: 52,
  pv: 35,
  badge: 'Best Value',
  image: "https://images.unsplash.com/photo-1556758174-b1d75b6d6876",
  alt: 'Premium supplement bottles arranged on clean white background',
  mariaNote: '"I take these every morning without fail. My energy levels are completely different."',
  inStock: true,
  featured: false
},
{
  id: 'mf-003',
  name: 'Éclat Brightening Mask',
  category: 'beauty',
  price: 48,
  memberPrice: 38,
  pv: 25,
  badge: null,
  image: "https://images.unsplash.com/photo-1532413301822-a5fe62c07854",
  alt: 'Luxurious face mask jar with pearl finish on soft pink background',
  mariaNote: '"My Sunday night ritual. Wakes up my skin for the week ahead."',
  inStock: true,
  featured: false
},
{
  id: 'mf-004',
  name: 'Harmonie Wellness Tea',
  category: 'wellness',
  price: 34,
  memberPrice: 27,
  pv: 18,
  badge: 'Popular',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_116fe75a5-1772116510217.png",
  alt: 'Premium herbal tea collection in elegant packaging with loose leaf herbs',
  mariaNote: '"My evening wind-down ritual. The blends are genuinely calming."',
  inStock: true,
  featured: false
},
{
  id: 'mf-005',
  name: 'Silk Body Ritual Oil',
  category: 'personal-care',
  price: 56,
  memberPrice: 45,
  pv: 30,
  badge: null,
  image: "https://images.unsplash.com/photo-1629384136243-640bf5eb8f51",
  alt: 'Amber glass bottle of body oil surrounded by botanical ingredients on wooden surface',
  mariaNote: '"Smells incredible, absorbs beautifully. A true luxury at this price."',
  inStock: false,
  featured: false
},
{
  id: 'mf-006',
  name: 'Equilibre Balance Complex',
  category: 'wellness',
  price: 78,
  memberPrice: 62,
  pv: 40,
  badge: 'Recommended',
  image: "https://images.unsplash.com/photo-1560553086-4184bd89f392",
  alt: 'Sophisticated supplement complex with clinical packaging on gradient background',
  mariaNote: '"My customers keep reordering this. The results speak for themselves."',
  inStock: true,
  featured: false
}];


export default function FeaturedProducts() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [wishlist, setWishlist] = useState<string[]>([]);

  const filtered = activeCategory === 'all' ?
  mariasFavorites :
  mariasFavorites.filter((p) => p.category === activeCategory);

  const toggleWishlist = (id: string) => {
    setWishlist((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleAddToCart = (name: string) => {
    toast.success(`${name} added to cart`, {
      description: 'Attributed to Maria Santos\'s store.'
    });
  };

  return (
    <section className="py-16 lg:py-24 bg-secondary">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="section-divider" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">Maria's Picks</span>
            </div>
            <h2 className="font-display text-section-title text-foreground">
              My Richésse Favorites
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) =>
            <button
              key={`store-cat-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeCategory === cat.id ?
              'bg-primary text-primary-foreground' :
              'bg-card text-muted-foreground border border-border hover:border-primary hover:text-primary'}`
              }>

                {cat.label}
              </button>
            )}
          </div>
        </div>

        {/* Products */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-5">
          {filtered.map((product) =>
          <div
            key={product.id}
            className="product-card card-elevated card-hover overflow-hidden group">

              {/* Image */}
              <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '4/5' }}>
                <AppImage
                src={product.image}
                alt={product.alt}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1280px) 33vw, 16vw"
                className="product-card-img object-cover" />

                <div className="absolute inset-0 bg-accent/0 group-hover:bg-accent/10 transition-colors duration-300" />

                {product.badge &&
              <span className="absolute top-3 left-3 richesse-badge bg-primary/15 text-primary text-[10px]">
                    {product.badge}
                  </span>
              }

                {!product.inStock &&
              <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                    <span className="text-sm font-semibold text-muted-foreground bg-card px-3 py-1.5 rounded-full border border-border">
                      Restocking Soon
                    </span>
                  </div>
              }

                <button
                onClick={() => toggleWishlist(product.id)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                aria-label="Add to wishlist">

                  <Icon
                  name="HeartIcon"
                  size={15}
                  className={wishlist.includes(product.id) ? 'text-rose-500' : 'text-muted-foreground'}
                  variant={wishlist.includes(product.id) ? 'solid' : 'outline'} />

                </button>
              </div>

              {/* Info */}
              <div className="p-4">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 capitalize">
                  {product.category.replace('-', ' ')}
                </p>
                <h3 className="text-sm font-semibold text-foreground mb-2 leading-snug">{product.name}</h3>

                {/* Maria's note */}
                <p className="text-[11px] text-muted-foreground italic leading-relaxed mb-3 line-clamp-2">
                  {product.mariaNote}
                </p>

                {/* Pricing */}
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-base font-bold text-foreground tabular-nums">${product.memberPrice}</span>
                  <span className="text-xs text-muted-foreground line-through tabular-nums">${product.price}</span>
                  <span className="text-[10px] font-medium text-primary ml-auto">{product.pv} PV</span>
                </div>

                <button
                onClick={() => product.inStock && handleAddToCart(product.name)}
                disabled={!product.inStock}
                className={`w-full py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                product.inStock ?
                'btn-primary' : 'bg-muted text-muted-foreground cursor-not-allowed'}`
                }>

                  {product.inStock ? 'Add to Cart' : 'Notify Me'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* View all */}
        <div className="text-center mt-10">
          <Link href="/product-catalog" className="btn-outline-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold">
            Browse Full Richésse Catalog
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>);

}